/**
 * Soft gate shown by <ZoomPanel> when a scribe recording ends with too little
 * transcript to draft a useful note. Warns but never blocks. Styled to match
 * <EndSessionPrompt>.
 */
const CORAL = '#e8624a'

export type ConversationTooShortModalProps = {
  /** Nothing captured at all (muted) vs. captured-but-thin — switches the copy (PD-1111). */
  empty?: boolean
  onKeepRecording: () => void
  onGenerateAnyway: () => void
  onDiscard: () => void
}

export function ConversationTooShortModal({
  empty,
  onKeepRecording,
  onGenerateAnyway,
  onDiscard,
}: ConversationTooShortModalProps) {
  return (
    <div
      data-testid="zoom-panel-too-short-prompt"
      role="dialog"
      aria-modal="true"
      aria-labelledby="zoom-panel-too-short-title"
      className="absolute inset-0 z-[1000] flex items-center justify-center bg-black/75 p-4"
    >
      <div className="w-full max-w-[290px] rounded-[14px] border border-white/10 bg-[#1e293b] px-5 py-6">
        <div
          id="zoom-panel-too-short-title"
          className="mb-2 text-center text-[15px] font-bold text-slate-100"
        >
          {empty ? 'No conversation captured' : 'Conversation may be too short'}
        </div>
        <div className="mb-5 text-center text-[12.5px] leading-[1.65] text-slate-400">
          {empty
            ? 'Nothing was transcribed during this session (participants may have stayed muted). Create a blank Progress Note to fill in manually, or discard the session.'
            : 'There isn’t much transcript yet to build a reliable progress note. Keep recording for a fuller note, or generate one now from what’s been captured.'}
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onKeepRecording}
            data-testid="zoom-panel-too-short-keep"
            className="w-full rounded-lg border-0 p-[10px] text-[13px] font-bold text-white"
            style={{ background: CORAL }}
          >
            Keep recording
          </button>
          <button
            type="button"
            onClick={onGenerateAnyway}
            data-testid="zoom-panel-too-short-generate"
            className="w-full rounded-lg border border-white/15 bg-transparent p-[9px] text-[12.5px] font-semibold text-slate-200 hover:bg-white/5"
          >
            {empty ? 'Create note anyway' : 'Generate anyway'}
          </button>
          <button
            type="button"
            onClick={onDiscard}
            data-testid="zoom-panel-too-short-discard"
            className="mt-1 w-full rounded-lg border-0 bg-transparent p-[7px] text-[12px] font-semibold text-[#f87171] hover:text-[#fca5a5]"
          >
            Discard
          </button>
        </div>
      </div>
    </div>
  )
}
